import { FullStory, init } from '@fullstory/browser'

import { Customer } from '@/features/auth/domain/customer'
import { User } from '@/features/auth/domain/user'

import { error } from '../logger'

interface IFullStoryAnalyticsService {
  readonly shouldRun: () => boolean
  readonly orgId: string
}

export class FullStoryAnalyticsService {
  private orgId: string
  private readonly shouldRun: () => boolean

  constructor(props: IFullStoryAnalyticsService) {
    this.orgId = props.orgId
    this.shouldRun = props.shouldRun
    if (!this.shouldRun()) {
      return
    }
    const isValidOrgId =
      this.orgId && typeof this.orgId === 'string' && this.orgId !== 'undefined' && this.orgId.trim().length > 0

    if (!isValidOrgId) {
      error('Invalid or missing FullStory organization ID')
      throw new Error('Invalid or missing FullStory organization ID')
    }

    try {
      init({
        orgId: this.orgId,
      })
    } catch (e) {
      if (e instanceof Error) {
        error('Failed to initialize FullStory analytics', { response: e })
      }
    }
  }

  private getFullName(user: User) {
    const nameParts = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
    return nameParts || ''
  }

  init(customer: Customer, user: User, loggedInAsCustomer: boolean, tokenRequestedFrom?: string) {
    try {
      FullStory('setIdentity', {
        uid: user.id,
        properties: {
          name: this.getFullName(user),
          email: user.email,
          emailDomain: user.email.split('@')[1],
          source: 'ship',
          customerId: customer._id,
          customerName: customer.name,
          userId: user.id,
          userLanguage: user.preferredLanguage,
          roles: user.roles ? user.roles.join(',') : '',
          enabledFeatures: customer.enabledFeatures ? customer.enabledFeatures.join(',') : '',
          tokenRequestedFrom,
          loggedInFromTms: loggedInAsCustomer,
          screenDimensions: `${window.screen.width} x ${window.screen.height}`,
        },
      })
    } catch (e) {
      if (e instanceof Error) {
        error('There has been an error during the initiation of fullstory analytics', { response: e })
      }
    }
  }

  reset = () => {
    if (!this.shouldRun()) {
      return
    }

    FullStory('setIdentity', {
      anonymous: true,
    })
  }

  sendEvent(name: string, data: Record<string, unknown> = {}) {
    if (!this.shouldRun()) {
      return
    }

    try {
      FullStory('trackEvent', {
        name: name,
        properties: data,
      })
    } catch (e) {
      if (e instanceof Error) {
        error('There has been an error during sending event to fullstory analytics', { response: e })
      }
    }
  }

  update(updates: Record<string, unknown>) {
    if (!this.shouldRun()) {
      return
    }

    try {
      FullStory('setProperties', {
        type: 'user',
        properties: updates,
      })
    } catch (e) {
      if (e instanceof Error) {
        error('There has been an error during updating fullstory analytics', { response: e })
      }
    }
  }
}
